<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { projectsApi } from '$lib/api/projects';
  import { getErrorMessage } from '$lib/api/client';
  import type { Project } from '$lib/types';

  const { value, onChange, disabled = false }: { value: number | null; onChange: (id: number | null) => void; disabled?: boolean } = $props();
  let search = $state('');
  let creating = $state(false);
  let name = $state('');
  let customer = $state('');
  let location = $state('');
  const client = useQueryClient();
  const projects = createQuery(() => ({ queryKey: ['projects'], queryFn: projectsApi.all }));
  const create = createMutation(() => ({
    mutationFn: () => projectsApi.create({ name: name.trim(), client_name: customer.trim(), location: location.trim() }),
    onSuccess: (project) => {
      client.setQueryData<Project[]>(['projects'], (old) => [...(old ?? []).filter((p) => p.id !== project.id), project]);
      client.invalidateQueries({ queryKey: ['projects'] });
      creating = false;
      name = ''; customer = ''; location = ''; search = '';
      onChange(project.id);
    }
  }));
  const options = $derived((projects.data ?? []).filter((p) => p.id === value || `${p.name} ${p.client_name} ${p.location}`.toLowerCase().includes(search.toLowerCase())));
</script>

<div class="flex flex-col gap-2">
  <div class="text-sm font-medium text-navy">Project group (optional)</div>
  <input class="input w-full" aria-label="Search project groups" placeholder="Find by project, customer or site…" bind:value={search} {disabled} />
  <div class="flex gap-2">
    <select class="input flex-1 min-w-0" aria-label="Project group" value={value ?? ''} disabled={disabled || create.isPending || projects.isLoading || projects.isError} onchange={(e) => {
      const next = e.currentTarget.value ? Number(e.currentTarget.value) : null;
      // Stay controlled while an assignment saves; failed requests must not
      // leave the browser showing a group that was never persisted.
      e.currentTarget.value = value === null ? '' : String(value);
      onChange(next);
    }}>
      <option value="">No project group</option>
      {#if value && !(projects.data ?? []).some((p) => p.id === value)}<option value={value}>Project #{value}</option>{/if}
      {#each options as project (project.id)}
        <option value={project.id}>{[project.name, project.client_name, project.location].filter(Boolean).join(' · ')}</option>
      {/each}
    </select>
    <button type="button" class="btn-secondary" {disabled} onclick={() => (creating = true)}>New project</button>
  </div>
  <p class="text-xs text-dark-gray">Grouping only; quotation details and pricing stay unchanged.</p>
  {#if projects.isLoading}<p class="text-xs text-dark-gray">Loading projects…</p>{/if}
  {#if projects.isError}<p class="tym-error-state">{getErrorMessage(projects.error)} <button class="btn-secondary" onclick={() => projects.refetch()}>Retry</button></p>{/if}
  {#if creating}
    <div class="border border-toyama-cloudy-gray rounded-lg p-3 flex flex-col gap-2">
      <label class="text-sm text-navy">Project name <input class="input w-full" bind:value={name} disabled={create.isPending} /></label>
      <label class="text-sm text-navy">Customer (optional) <input class="input w-full" bind:value={customer} disabled={create.isPending} /></label>
      <label class="text-sm text-navy">Site / location (optional) <input class="input w-full" bind:value={location} disabled={create.isPending} /></label>
      {#if create.isError}<p class="tym-error-state">{getErrorMessage(create.error)}</p>{/if}
      <div class="flex gap-2">
        <button type="button" class="btn-primary" disabled={disabled || !name.trim() || create.isPending} onclick={() => create.mutate()}>{create.isPending ? 'Creating…' : 'Create and select'}</button>
        <button type="button" class="btn-secondary" disabled={create.isPending} onclick={() => (creating = false)}>Cancel</button>
      </div>
    </div>
  {/if}
</div>
